import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function GapAnalysisScorecard() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  useEffect(() => {
    fetch(`${API_URL}/api/gap-analysis/${analysisId}`)
      .then(res => res.json())
      .then(data => {
        setAnalysis(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [analysisId]);
  
  const handleGenerateBlueprint = () => {
    // Store gap analysis data in sessionStorage and redirect to blueprint admin
    sessionStorage.setItem('gap_analysis_for_blueprint', JSON.stringify(analysis));
    window.open('/admin/blueprint?from_gap=' + analysisId, '_blank');
  };
  
  if (loading) return <div className="p-8">Loading...</div>;
  if (!analysis) return <div className="p-8">Not found</div>;
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0a2540] text-white p-8">
        <p className="text-[#d4af37] text-sm mb-2">Gap Analysis Report</p>
        <h1 className="text-3xl font-bold">{analysis.business_name}</h1>
        <p className="text-slate-300">{analysis.primary_location} • {analysis.services?.join(', ')}</p>
        <div className="mt-6 flex items-center gap-6">
          <div className={`rounded-xl p-4 text-center ${analysis.overall_status === 'red' ? 'bg-red-500' : analysis.overall_status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}>
            <p className="text-sm opacity-80">Current Score</p>
            <p className="text-4xl font-bold">{analysis.overall_score}/100</p>
          </div>
          <div className="flex gap-4">
            <div className="bg-white/10 rounded p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{analysis.critical_findings}</p>
              <p className="text-xs text-slate-300">Critical</p>
            </div>
            <div className="bg-white/10 rounded p-3 text-center">
              <p className="text-2xl font-bold text-yellow-400">{analysis.warning_findings}</p>
              <p className="text-xs text-slate-300">Warnings</p>
            </div>
            <div className="bg-white/10 rounded p-3 text-center">
              <p className="text-2xl font-bold">{analysis.total_findings}</p>
              <p className="text-xs text-slate-300">Total</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-8 max-w-4xl mx-auto">
        {/* Executive Summary */}
        <div className="bg-white rounded-lg p-6 mb-4 border">
          <h2 className="text-xl font-bold text-[#0a2540] mb-3">Executive Summary</h2>
          <p className="text-slate-600 whitespace-pre-line leading-relaxed">{analysis.executive_summary}</p>
        </div>
        
        {/* Top Gaps */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-red-700 mb-3">🚨 Top Priority Gaps</h2>
          <ol className="space-y-2">
            {analysis.top_5_gaps?.map((gap, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center flex-shrink-0">{i+1}</span>
                <span className="text-slate-700">{gap}</span>
              </li>
            ))}
          </ol>
        </div>
        
        {/* Quick Wins */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-green-700 mb-3">🚀 Quick Wins</h2>
          <ul className="space-y-2">
            {analysis.quick_wins?.map((win, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700">
                <span className="text-green-600">✓</span>
                {win}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Category Breakdown */}
        <h2 className="text-xl font-bold text-[#0a2540] mb-4">Category Breakdown</h2>
        <div className="space-y-2 mb-8">
          {analysis.category_scores?.map((cat) => (
            <div key={cat.category} className="bg-white rounded-lg p-4 border">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold text-[#0a2540]">{cat.category_name}</span>
                  <p className="text-sm text-slate-500 mt-1">{cat.summary}</p>
                </div>
                <span className={`px-4 py-2 rounded-lg text-white font-bold ${cat.status === 'red' ? 'bg-red-500' : cat.status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}>
                  {cat.score}/100
                </span>
              </div>
              {cat.key_gaps?.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm font-medium text-red-600 mb-1">Key Gaps:</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {cat.key_gaps.slice(0, 2).map((gap, i) => (
                      <li key={i}>• {gap}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* CTA Section - Two Options */}
        <div className="bg-gradient-to-r from-[#0a2540] to-[#162536] text-white p-8 rounded-lg">
          <h2 className="text-2xl font-bold mb-2 text-center">Ready to Close These Gaps?</h2>
          <p className="text-slate-300 text-center mb-6">
            Your analysis identified {analysis.critical_findings} critical issues and {analysis.warning_findings} warnings.
            Get your personalized 12-month action plan.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleGenerateBlueprint}
              disabled={generating}
              className="bg-[#d4af37] text-[#0a2540] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#b5952f] transition-colors"
            >
              {generating ? 'Generating...' : '📋 Generate 12-Month Blueprint'}
            </button>
            <a 
              href="https://cal.com/mark-earle-6tzgnf/60min" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white/10 border border-white/30 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transition-colors text-center"
            >
              📞 Book Strategy Call
            </a>
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-8 text-slate-400 text-sm">
          <p>Prepared by PromptReady • {analysis.created_at?.slice(0, 10)}</p>
        </div>
      </div>
    </div>
  );
}

export default GapAnalysisScorecard;
